import type {
  SearchPagination,
  SearchParamsObject,
  SearchResponse,
} from '@algolia/client-search';

import { configString } from '#/config/appConfigUtils';
import type { OrgDashboardAlgoliaHit } from '#/modules/orgDashboard/algolia/types/OrgDashboardAlgoliaHit';
import { getSearchAlgoliaConfigWithApiKey } from '#/modules/search/algolia/searchAlgoliaConfig';
import { algoliaSearch } from '#/utils/algolia/algoliaSearch';

export type OrgDashboardManageListingsSearchResultType =
  | 'JOB'
  | 'INTERNSHIP'
  | 'EVENT'
  | 'VOLUNTEER';

export type OrgDashboardManageListingsSearchResult = {
  result:
    | (SearchResponse<OrgDashboardAlgoliaHit> & Required<SearchPagination>)
    | null;
  validTypes: OrgDashboardManageListingsSearchResultType[];
  validStatuses: string[];
};

function getQueryOptions({
  type,
  status,
  pageIndex,
}: {
  type: string;
  status: string;
  pageIndex: number;
}): SearchParamsObject {
  const filters = [];

  if (type === 'VOLOP') {
    filters.push(`type:'${type}' AND actionType:'VOLOP'`);
  } else if (type !== 'ALL') {
    filters.push(`type:'${type}'`);
  }

  if (status !== 'ALL') {
    filters.push(`status:'${status}'`);
  }

  const attributesToRetrieve = [
    'objectID',
    'type',
    'status',
    'name',
    'city',
    'state',
    'country',
    'published',
    'created',
    'expires',
    'updated',
    'analyticMetrics',
    'hasNewActivity',
    'canPromote',
    'isPromoted',
    'hasAts',
  ];

  return {
    hitsPerPage: 20,
    page: pageIndex,
    attributesToRetrieve,
    filters: filters.join(' AND '),
  } satisfies SearchParamsObject;
}

type Args = {
  searchApiKey: string;
  pageIndex: number;
  status: string;
  query: string;
  type: string;
  isUsingAlgoliaMock: boolean;
};

export async function orgDashboardManageListingsIsomorphicSearch({
  searchApiKey,
  pageIndex,
  status,
  query,
  type,
  isUsingAlgoliaMock,
}: Args): Promise<OrgDashboardManageListingsSearchResult> {
  const config = getSearchAlgoliaConfigWithApiKey(searchApiKey);
  const queryOptions = getQueryOptions({ type, status, pageIndex });

  const [result, facetsResult] = await Promise.all([
    algoliaSearch<OrgDashboardAlgoliaHit>({
      config,
      query,
      indexName: configString('algolia', 'orgIndexName'),
      options: queryOptions,
      analytics: true,
      isUsingAlgoliaMock,
    }).catch(() => null),

    algoliaSearch({
      config,
      query: '',
      indexName: configString('algolia', 'orgIndexName'),
      options: {
        attributesToRetrieve: ['type', 'status'],
        hitsPerPage: 0,
        facets: ['type', 'status'],
      },
      analytics: false,
      isUsingAlgoliaMock,
    }).catch(() => null),
  ]);

  const validTypes = Object.keys(
    facetsResult?.facets?.type ?? {},
  ) as OrgDashboardManageListingsSearchResultType[];
  const validStatuses = Object.keys(facetsResult?.facets?.status ?? {});

  return { result, validTypes, validStatuses };
}
